import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Megaphone, Building2, Inbox, Handshake, Calendar, Users, Ticket, BarChart3, Folder, Settings, Search, Plus, Bell, Sparkles, ChevronLeft, ChevronRight, ChevronDown, Check, UserPlus, FileSignature, Receipt, User, Repeat, Keyboard, HelpCircle, LogOut } from 'lucide-react';
const navItems = [{
  icon: <LayoutDashboard className="w-5 h-5" data-id="element-556" />,
  label: 'Home',
  path: '/app',
  badge: null
}, {
  icon: <Megaphone className="w-5 h-5" data-id="element-557" />,
  label: 'Campaigns',
  path: '/app/campaigns',
  badge: null
}, {
  icon: <Building2 className="w-5 h-5" data-id="element-558" />,
  label: 'Venues',
  path: '/app/venues',
  badge: null
}, {
  icon: <Inbox className="w-5 h-5" data-id="element-559" />,
  label: 'Inbox',
  path: '/app/inbox',
  badge: 3
}, {
  icon: <Handshake className="w-5 h-5" data-id="element-560" />,
  label: 'Deals',
  path: '/app/deals',
  badge: null
}, {
  icon: <Calendar className="w-5 h-5" data-id="element-561" />,
  label: 'Schedule',
  path: '/app/schedule',
  badge: null
}, {
  icon: <Users className="w-5 h-5" data-id="element-562" />,
  label: 'Lineup',
  path: '/app/lineup',
  badge: null
}, {
  icon: <Ticket className="w-5 h-5" data-id="element-563" />,
  label: 'Shows',
  path: '/app/shows',
  badge: null
}, {
  icon: <BarChart3 className="w-5 h-5" data-id="element-564" />,
  label: 'Analytics',
  path: '/app/analytics',
  badge: null
}, {
  icon: <Folder className="w-5 h-5" data-id="element-565" />,
  label: 'Assets',
  path: '/app/assets',
  badge: null
}];
export function AppLayout({
  children
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  // Determine page title
  const currentNavItem = navItems.find(item => item.path === location.pathname);
  let pageTitle = 'Home';
  if (currentNavItem) {
    pageTitle = currentNavItem.label;
  } else if (location.pathname.startsWith('/app/settings')) {
    pageTitle = 'Settings';
  }
  return <div className="min-h-screen bg-background flex" data-id="element-566">
      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-16' : 'w-64'} border-r border-border flex flex-col transition-all duration-300 shrink-0`} data-id="element-567">
        {/* Logo */}
        <div className="h-16 border-b border-border flex items-center px-4" data-id="element-568">
          {!collapsed && <Link to="/app" className="flex items-center gap-2" data-id="element-569">
              <span className="text-xl font-bold text-textMain" data-id="element-570">Booker</span>
              <div className="w-1.5 h-1.5 rounded-full bg-primary" data-id="element-571"></div>
            </Link>}
          {collapsed && <Link to="/app" className="flex items-center justify-center w-full" data-id="element-572">
              <div className="w-2 h-2 rounded-full bg-primary" data-id="element-573"></div>
            </Link>}
        </div>

        {/* Nav Items */}
        <nav className="flex-1 p-3 overflow-y-auto" data-id="element-574">
          <div className="space-y-1" data-id="element-575">
            {navItems.map(item => {
            const isActive = location.pathname === item.path;
            return <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors relative ${isActive ? 'bg-primary/10 text-primary' : 'text-textMuted hover:text-textMain hover:bg-surfaceHover'}`} title={collapsed ? item.label : undefined} data-id="element-576">
                  {item.icon}
                  {!collapsed && <span className="text-sm font-medium" data-id="element-577">{item.label}</span>}
                  {!collapsed && item.badge && <span className="ml-auto w-5 h-5 rounded-full bg-primary text-background text-xs font-bold flex items-center justify-center" data-id="element-578">
                      {item.badge}
                    </span>}
                  {collapsed && item.badge && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-background text-[10px] font-bold flex items-center justify-center" data-id="element-579">
                      {item.badge}
                    </span>}
                </Link>;
          })}
          </div>
        </nav>

        {/* Settings */}
        <div className="p-3 border-t border-border" data-id="element-580">
          <Link to="/app/settings" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-textMuted hover:text-textMain hover:bg-surfaceHover transition-colors" title={collapsed ? 'Settings' : undefined} data-id="element-581">
            <Settings className="w-5 h-5" data-id="element-582" />
            {!collapsed && <span className="text-sm font-medium" data-id="element-583">Settings</span>}
          </Link>
        </div>

        {/* Collapse Toggle */}
        <div className="p-3 border-t border-border" data-id="element-584">
          <button onClick={() => setCollapsed(!collapsed)} className="w-full flex items-center justify-center px-3 py-2.5 rounded-lg text-textMuted hover:text-textMain hover:bg-surfaceHover transition-colors" data-id="element-585">
            {collapsed ? <ChevronRight className="w-5 h-5" data-id="element-586" /> : <ChevronLeft className="w-5 h-5" data-id="element-587" />}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0" data-id="element-588">
        {/* Top Bar */}
        <header className="h-16 border-b border-border flex items-center justify-between px-6 shrink-0" data-id="element-589">
          {/* Page Title & Workspace Switcher */}
          <div className="flex items-center gap-4" data-id="element-590">
            <div className="relative" data-id="element-591">
              <button onClick={() => setWorkspaceOpen(!workspaceOpen)} className="flex items-center gap-2 px-3 py-1.5 bg-surface border border-border rounded-lg hover:bg-surfaceHover transition-colors" data-id="element-592">
                <div className="w-5 h-5 rounded bg-primary/20 flex items-center justify-center text-primary text-xs font-bold" data-id="element-593">
                  W
                </div>
                <span className="text-sm font-medium" data-id="element-594">The Wildwoods</span>
                <ChevronDown className="w-4 h-4 text-textMuted" data-id="element-595" />
              </button>
              {workspaceOpen && <div className="absolute top-full left-0 mt-2 w-64 bg-surface border border-border rounded-xl shadow-xl overflow-hidden z-50" data-id="element-596">
                  <div className="p-2" data-id="element-597">
                    <div className="text-xs font-semibold text-textMuted px-2 py-1.5 uppercase tracking-wider" data-id="element-598">
                      Artist Workspaces
                    </div>
                    <button className="w-full flex items-center gap-3 px-2 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left" data-id="element-599">
                      <div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center text-primary text-xs font-bold" data-id="element-600">
                        W
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-601">
                        <div className="text-sm font-medium truncate" data-id="element-602">
                          The Wildwoods
                        </div>
                        <div className="text-xs text-textMuted truncate" data-id="element-603">
                          Folk / Americana
                        </div>
                      </div>
                      <Check className="w-4 h-4 text-primary" data-id="element-604" />
                    </button>
                    <button className="w-full flex items-center gap-3 px-2 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left" data-id="element-605">
                      <div className="w-6 h-6 rounded bg-blue-500/20 flex items-center justify-center text-blue-500 text-xs font-bold" data-id="element-606">
                        S
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-607">
                        <div className="text-sm font-medium truncate" data-id="element-608">
                          Sarah & The Sundays
                        </div>
                        <div className="text-xs text-textMuted truncate" data-id="element-609">
                          Indie Pop
                        </div>
                      </div>
                    </button>
                    <div className="h-px bg-border my-2" data-id="element-610"></div>
                    <div className="text-xs font-semibold text-textMuted px-2 py-1.5 uppercase tracking-wider" data-id="element-611">
                      Venue Workspaces
                    </div>
                    <button className="w-full flex items-center gap-3 px-2 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left" data-id="element-612">
                      <div className="w-6 h-6 rounded bg-purple-500/20 flex items-center justify-center text-purple-500 text-xs font-bold" data-id="element-613">
                        E
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-614">
                        <div className="text-sm font-medium truncate" data-id="element-615">
                          The Empty Bottle
                        </div>
                        <div className="text-xs text-textMuted truncate" data-id="element-616">
                          Chicago, IL
                        </div>
                      </div>
                    </button>
                  </div>
                </div>}
            </div>
            <div className="w-px h-6 bg-border" data-id="element-617"></div>
            <h1 className="text-xl font-semibold" data-id="element-618">{pageTitle}</h1>
          </div>

          {/* Search Bar */}
          <div className="flex-1 max-w-xl mx-8" data-id="element-619">
            <div className="relative" data-id="element-620">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textMuted" data-id="element-621" />
              <input type="text" placeholder="Search artists, venues, deals, shows..." className="w-full pl-10 pr-12 py-2 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary transition-colors" data-id="element-622" />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1" data-id="element-623">
                <kbd className="hidden sm:inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-medium text-textMuted bg-background border border-border rounded" data-id="element-624">
                  ⌘
                </kbd>
                <kbd className="hidden sm:inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-medium text-textMuted bg-background border border-border rounded" data-id="element-625">
                  K
                </kbd>
              </div>
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3" data-id="element-626">
            <div className="relative" data-id="element-627">
              <button onClick={() => {
              setNewMenuOpen(!newMenuOpen);
              setProfileMenuOpen(false);
            }} className="w-9 h-9 rounded-lg bg-primary text-background flex items-center justify-center hover:bg-primaryHover transition-colors shadow-[0_0_10px_rgba(245,158,11,0.2)]" data-id="element-628">
                <Plus className="w-5 h-5" data-id="element-629" />
              </button>
              {newMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-surface border border-border rounded-xl shadow-xl overflow-hidden z-50" data-id="element-630">
                  <div className="p-2 space-y-0.5" data-id="element-631">
                    <button onClick={() => {
                  navigate('/app/profile');
                  setNewMenuOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-632">
                      <Users className="w-4 h-4 text-textMuted" data-id="element-633" /> New Artist
                    </button>
                    <button onClick={() => {
                  setNewMenuOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-634">
                      <UserPlus className="w-4 h-4 text-textMuted" data-id="element-635" /> New Band
                      Member
                    </button>
                    <button onClick={() => {
                  navigate('/app/campaigns/new');
                  setNewMenuOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-636">
                      <Megaphone className="w-4 h-4 text-textMuted" data-id="element-637" /> New
                      Campaign
                    </button>
                    <button onClick={() => {
                  navigate('/app/event-needs/new');
                  setNewMenuOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-638">
                      <Building2 className="w-4 h-4 text-textMuted" data-id="element-639" /> New Event
                      Need
                    </button>
                    <button onClick={() => {
                  navigate('/app/deals');
                  setNewMenuOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-640">
                      <Handshake className="w-4 h-4 text-textMuted" data-id="element-641" /> New Deal
                    </button>
                    <button onClick={() => {
                  navigate('/app/contracts');
                  setNewMenuOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-642">
                      <FileSignature className="w-4 h-4 text-textMuted" data-id="element-643" /> New
                      Contract
                    </button>
                    <button onClick={() => {
                  navigate('/app/invoices');
                  setNewMenuOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-644">
                      <Receipt className="w-4 h-4 text-textMuted" data-id="element-645" /> New Invoice
                    </button>
                    <button onClick={() => {
                  navigate('/app/shows');
                  setNewMenuOpen(false);
                }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-646">
                      <Ticket className="w-4 h-4 text-textMuted" data-id="element-647" /> New Show
                    </button>
                  </div>
                </div>}
            </div>

            <Link to="/app/notifications" className="w-9 h-9 rounded-lg bg-surface border border-border flex items-center justify-center hover:bg-surfaceHover transition-colors relative" data-id="element-648">
              <Bell className="w-5 h-5 text-textMuted" data-id="element-649" />
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary" data-id="element-650"></span>
            </Link>
            <button className="w-9 h-9 rounded-lg bg-surface border border-border flex items-center justify-center hover:bg-surfaceHover transition-colors" data-id="element-651">
              <Sparkles className="w-5 h-5 text-primary" data-id="element-652" />
            </button>

            <div className="relative" data-id="element-653">
              <button onClick={() => {
              setProfileMenuOpen(!profileMenuOpen);
              setNewMenuOpen(false);
            }} className="w-9 h-9 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center text-sm font-bold text-primary hover:bg-primary/30 transition-colors" data-id="element-654">
                M
              </button>
              {profileMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-surface border border-border rounded-xl shadow-xl overflow-hidden z-50" data-id="element-655">
                  <div className="p-4 border-b border-border" data-id="element-656">
                    <div className="font-medium" data-id="element-657">Mark Johnson</div>
                    <div className="text-xs text-textMuted" data-id="element-658">
                      mark@thewildwoodsband.com
                    </div>
                  </div>
                  <div className="p-2 space-y-0.5" data-id="element-659">
                    <Link to="/account/profile" onClick={() => setProfileMenuOpen(false)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-660">
                      <User className="w-4 h-4 text-textMuted" data-id="element-661" /> Profile
                    </Link>
                    <Link to="/workspace-select" onClick={() => setProfileMenuOpen(false)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-662">
                      <Repeat className="w-4 h-4 text-textMuted" data-id="element-663" /> Workspace
                      Switch
                    </Link>
                    <Link to="/account/preferences" onClick={() => setProfileMenuOpen(false)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-664">
                      <Bell className="w-4 h-4 text-textMuted" data-id="element-665" /> Notification
                      Preferences
                    </Link>
                    <Link to="/app/settings" onClick={() => setProfileMenuOpen(false)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-666">
                      <Keyboard className="w-4 h-4 text-textMuted" data-id="element-667" /> Keyboard
                      Shortcuts
                    </Link>
                    <Link to="/contact" onClick={() => setProfileMenuOpen(false)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm" data-id="element-668">
                      <HelpCircle className="w-4 h-4 text-textMuted" data-id="element-669" /> Support
                    </Link>
                    <div className="h-px bg-border my-1" data-id="element-670"></div>
                    <Link to="/login" onClick={() => setProfileMenuOpen(false)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surfaceHover rounded-lg transition-colors text-left text-sm text-red-500" data-id="element-671">
                      <LogOut className="w-4 h-4" data-id="element-672" /> Logout
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6" data-id="element-673">{children}</main>
      </div>
    </div>;
}